import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import '../components/Navbar.css';
import '../components/HeroCards.css';

function CommitteesPage() {
  document.title = 'Florida Elections - Committees';
  
  const [data, setData] = useState([]);
  const [balances, setBalances] = useState([]); // State for balances
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data on mount (all committees, no filters)
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch committees first
        const committeesResponse = await fetch('/api/committees');
        if (!committeesResponse.ok) {
          throw new Error(`HTTP error for committees! status: ${committeesResponse.status}`);
        }
        const committeesResult = await committeesResponse.json();
        console.log('Fetched committees:', committeesResult); // Debug: Log committees data
        console.log('Committee AcctNums:', committeesResult.map(c => ({ AcctNum: c.AcctNum, type: typeof c.AcctNum }))); // Debug: Log AcctNums and types
        setData(committeesResult);

        // Extract AcctNums and fetch balances for those specific accounts
        const acctNums = committeesResult.map(c => c.AcctNum).filter(num => num != null);
        if (acctNums.length > 0) {
          const balancesResponse = await fetch(`/api/balances?accounts=${acctNums.join(',')}`);
          if (balancesResponse.ok) {
            const balancesResult = await balancesResponse.json();
            console.log('Fetched balances:', balancesResult); // Debug: Log balances data
            console.log('Balance accounts:', balancesResult.map(b => ({ account: b.account, type: typeof b.account }))); // Debug: Log accounts and types
            setBalances(balancesResult);
          } else {
            console.error(`HTTP error for balances! status: ${balancesResponse.status}`);
            setBalances([]);
          }
        } else {
          setBalances([]);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Memoized balances map for quick lookup by account (AcctNum)
  const balancesMap = useMemo(() => {
    const map = new Map(balances.map(balance => [String(balance.account).trim(), balance.coh]));
    console.log('Balances Map keys:', Array.from(map.keys())); // Debug: Log map keys
    return map;
  }, [balances]);

  // Memoized computed fields & sorting (by AcctNum DESC)
  const processedData = useMemo(() => {
    const mappedData = data.map(row => {
      const key = String(row.AcctNum).trim();
      const cohValue = balancesMap.get(key);
      console.log(`Lookup for AcctNum "${row.AcctNum}" (key: "${key}"):`, cohValue); // Debug: Log each lookup
      return {
        ...row,
        fullName: `${row.ChrNameFirst || ''} ${row.ChrNameMiddle ? row.ChrNameMiddle + ' ' : ''}${row.ChrNameLast || ''}`.trim(), // Adapt to committee name fields if different
        coh: cohValue || null // Join coh from balances (keyed by account/AcctNum)
      };
    });

    return mappedData.sort((a, b) => b.AcctNum - a.AcctNum); // Sort by AcctNum DESC (numeric)
  }, [data, balancesMap]);

  // Column widths for better sizing (adapted for committees; adjust as needed)
  const columnWidths = {
    account: '80px',
    name: '300px', // Wider for committee names
    type: '150px', // Assuming a Type column
    chair: '100px',
    coh: '120px'
  };

  if (loading) return <div style={{ background: '#fff', minHeight: '100vh' }}><Navbar /><main style={{ maxWidth: '900px', margin: '8px auto', padding: '12px' }}>Loading committee data...</main></div>;
  if (error) return <div style={{ background: '#fff', minHeight: '100vh' }}><Navbar /><main style={{ maxWidth: '900px', margin: '8px auto', padding: '12px' }}>Error: {error}</main></div>;

  return (
    <div style={{ background: '#ffffff', minHeight: '100vh', color: '#000' }}>
      <Navbar />

  <main style={{ maxWidth: '1000px', margin: '8px auto', padding: '12px' }}>
        <section style={{ padding: '10px 12px', borderRadius: 12, boxShadow: '0 1px 6px rgba(16,24,40,0.04)' }}>
          <h2 style={{ marginTop: 0 }}>Florida Election Committees</h2>

          {/* Wrapper for table to center and constrain width */}
          <div style={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
            <table 
              border="1" 
              style={{ 
                borderCollapse: 'collapse', 
                tableLayout: 'fixed', // Fixed layout for consistent column widths
                width: 'auto', // Auto width based on column sums
                minWidth: '750px', // Minimum to fit all columns
                margin: '10px auto' 
              }}
            >
              <thead>
                <tr style={{ backgroundColor: '#f2f2f2' }}>
                  <th style={{ width: columnWidths.account, padding: '8px' }}>Account #</th>
                  <th style={{ width: columnWidths.name, padding: '8px' }}>Committee Name</th>
                  <th style={{ width: columnWidths.type, padding: '8px' }}>Type</th> {/* Adapt if field exists */}
                  <th style={{ width: columnWidths.chair, padding: '8px' }}>Chair</th>
                  <th style={{ width: columnWidths.coh, padding: '8px' }}>Cash On Hand</th>
                </tr>
              </thead>
              <tbody>
                {processedData.length > 0 ? (
                  processedData.map((row, index) => (
                    <tr key={row._id || row.AcctNum || index} style={{ backgroundColor: index % 2 === 0 ? '#e6f3ff' : 'transparent' }}> {/* Alternating rows */}
                      <td style={{ padding: '8px' }}>
                        <Link to={`/committees/${row.AcctNum}`} style={{ color: '#007bff', textDecoration: 'none' }}>
                          {row.AcctNum}
                        </Link>
                      </td>
                      <td style={{ padding: '4px' }}>{row.Name}</td>
                      <td style={{ padding: '4px' }}>{row.Type || ''}</td> {/* Adapt field name */}
                      <td style={{ padding: '4px' }}>{row.fullName}</td>
                      <td style={{ padding: '4px' }}>
                        {row.coh !== null 
                          ? `$${Number(row.coh).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                          : ''
                        }
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ padding: '8px', textAlign: 'center' }}>No committees found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

export default CommitteesPage;